import { Trophy, Star, Banknote } from "lucide-react";
import { formatNumber, getTickerCurrency } from "../utils/trading";

const TT = {
  LT: { l: "LT", c: "text-cyan-400 bg-cyan-500/15 border-cyan-500/25" },
  SWING: { l: "SW", c: "text-amber-400 bg-amber-500/15 border-amber-500/25" },
  SWING_COURT: { l: "SWC", c: "text-orange-400 bg-orange-500/15 border-orange-500/25" },
  SCALP: { l: "SCA", c: "text-rose-400 bg-rose-500/15 border-rose-500/25" },
  WATCH: { l: "W", c: "text-slate-400 bg-slate-500/10 border-slate-500/20" },
};
const SIG = {
  STRONG_BUY: "text-emerald-400 bg-emerald-500/15 border-emerald-500/25",
  BUY: "text-green-400 bg-green-500/10 border-green-500/20",
  HOLD: "text-amber-400 bg-amber-500/10 border-amber-500/20",
  SELL: "text-red-400 bg-red-500/10 border-red-500/20",
  STRONG_SELL: "text-red-400 bg-red-500/10 border-red-500/20",
  AVOID: "text-red-400 bg-red-500/10 border-red-500/20",
};
const MEDALS = ["text-amber-400", "text-slate-300", "text-orange-500", "text-slate-400", "text-slate-500"];
const C = { EUR: "\u20ac", GBP: "\u00a3", CHF: "CHF", SEK: "kr", NOK: "kr", DKK: "kr", HKD: "HK$", JPY: "\u00a5", USD: "$" };

export default function TopConvictionsCompact({ topRated, onAnalyze, title = "Top 5 Convictions" }) {
  if (!topRated || topRated.length === 0) {
    return (
      <div className="glass-card rounded-xl p-3" data-testid="top-convictions-compact">
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="w-3.5 h-3.5 text-amber-400" />
          <span className="text-xs font-bold">{title}</span>
        </div>
        <div className="text-[10px] text-slate-500 text-center py-4">Aucune donnee disponible</div>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-xl p-3" data-testid="top-convictions-compact">
      <div className="flex items-center gap-1.5 mb-2">
        <Trophy className="w-3.5 h-3.5 text-amber-400" />
        <span className="text-xs font-bold">{title}</span>
        <span className="text-[9px] text-slate-500 ml-auto">{topRated.length}</span>
      </div>
      <div className="space-y-px">
        {topRated.map((stock, i) => {
          const curr = C[stock.currency] || getTickerCurrency(stock.ticker);
          const sp = stock.sparkline || [];
          const spMin = sp.length > 0 ? Math.min(...sp) : 0;
          const spR = (sp.length > 0 ? Math.max(...sp) : 1) - spMin || 1;
          const m1m = stock.momentum_1m ?? (sp.length >= 2 ? ((sp[sp.length - 1] - sp[0]) / sp[0] * 100) : null);
          const tt = TT[stock.trade_type];
          const sig = SIG[stock.tech_signal] || SIG.HOLD;

          return (
            <div key={stock.ticker}
              onClick={() => onAnalyze?.(stock.ticker)}
              className="flex items-center gap-1.5 px-1.5 py-1 rounded hover:bg-slate-700/40 cursor-pointer transition-colors group"
              data-testid={`compact-conviction-${stock.ticker}`}>
              <span className={`text-[11px] font-black w-3 shrink-0 ${MEDALS[i]}`}>{i + 1}</span>
              {sp.length > 2 ? (
                <svg viewBox={`0 0 ${sp.length - 1} 10`} className="w-8 h-2.5 shrink-0" preserveAspectRatio="none">
                  <path d={`M0,${10 - (sp[0] - spMin) / spR * 10} ${sp.map((v, j) => `L${j},${10 - (v - spMin) / spR * 10}`).join(" ")}`}
                    fill="none" stroke={m1m >= 0 ? "#34d399" : "#f87171"} strokeWidth="1.2" />
                </svg>
              ) : <div className="w-8 h-2.5 shrink-0" />}
              <span className="font-mono text-[10px] font-bold text-cyan-400 group-hover:text-cyan-300 truncate min-w-0">{stock.ticker}</span>
              <span className={`text-[6px] font-bold px-0.5 rounded border shrink-0 leading-tight ${sig}`}>
                {(stock.tech_signal || "").replace("STRONG_", "S.").replace("_", " ")}
              </span>
              {tt && <span className={`text-[6px] font-bold px-0.5 rounded border shrink-0 leading-tight ${tt.c}`}>{tt.l}</span>}
              <span className="ml-auto font-mono text-[10px] text-white shrink-0">{curr}{formatNumber(stock.price)}</span>
              <span className={`font-mono text-[9px] font-bold shrink-0 w-9 text-right ${m1m >= 0 ? "text-emerald-400" : m1m < 0 ? "text-red-400" : "text-slate-500"}`}>
                {m1m !== null ? `${m1m >= 0 ? "+" : ""}${typeof m1m === 'number' ? m1m.toFixed(1) : m1m}%` : ""}
              </span>
              <div className="flex items-center gap-px shrink-0">
                <Star className="w-2 h-2 text-amber-400" />
                <span className="text-[8px] font-bold text-amber-400">{stock.composite}</span>
              </div>
              {stock.recommended_investment > 0 && (
                <span className="text-[8px] font-mono text-emerald-400/70 shrink-0">{stock.recommended_investment >= 1000 ? `${Math.round(stock.recommended_investment / 1000)}k€` : `${stock.recommended_investment}€`}</span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
